import { ReactNode, Ref, forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useDrawerScrollElement } from '~/features/productList/components/VirtualizedList';
import { AlphabeticGroups } from '~/features/productList/hooks/useAlphabeticGroups';
import styles from './AlphabeticVirtualizedList.module.scss';

type Row<T> =
    | { type: 'pinned'; item: T }
    | { type: 'header'; letter: string }
    | { type: 'item'; item: T };

export type AlphabeticVirtualizedListHandle = {
    scrollToLetter: (letter: string, behavior?: ScrollBehavior) => void;
};

const ROW_HEIGHT = 40;

type Props<T> = {
    groups: AlphabeticGroups<T>;
    itemKey: (item: T) => string;
    /** `isSectionEnd` marks the last row of a letter's section, for a darker section-break divider. */
    renderItem: (item: T, meta: { isSectionEnd: boolean }) => ReactNode;
    /** False when this facet is hidden but still mounted, so it detaches from the shared mobile scroll container. */
    isActive?: boolean;
};

function AlphabeticVirtualizedListInner<T>(
    { groups, itemKey, renderItem, isActive = true }: Props<T>,
    ref: Ref<AlphabeticVirtualizedListHandle>,
) {
    const scrollElement = useDrawerScrollElement();

    const { rows, letterIndex } = useMemo(() => {
        const rows: Row<T>[] = groups.pinned.map((item) => ({ type: 'pinned', item }));
        const letterIndex = new Map<string, number>();

        groups.sections.forEach((section) => {
            letterIndex.set(section.letter, rows.length);
            rows.push({ type: 'header', letter: section.letter });
            section.items.forEach((item) => rows.push({ type: 'item', item }));
        });

        return { rows, letterIndex };
    }, [groups]);

    // No measureElement ref: rows are fixed-height, so the estimate stays exact for scrollToLetter's math below.
    const virtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => (isActive ? scrollElement : null),
        estimateSize: () => ROW_HEIGHT,
        getItemKey: (index) => {
            const row = rows[index];
            return row.type === 'header' ? `header-${row.letter}` : itemKey(row.item);
        },
        overscan: 8,
        // scrollTo({behavior:'instant'}) bypasses the Drawer's scroll-behavior:smooth; must stay offset-free since tanstack re-invokes this repeatedly.
        scrollToFn: (offset, { adjustments = 0 }, instance) => {
            const el = instance.scrollElement;
            if (!el) return;

            el.scrollTo({ top: offset + adjustments, left: 0, behavior: 'instant' });
        },
    });

    // react-virtual re-applies a cached scroll offset on reattach, so force a reset instead.
    const wasActiveRef = useRef(isActive);

    useEffect(() => {
        if (isActive && !wasActiveRef.current) {
            scrollElement?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }

        wasActiveRef.current = isActive;
    }, [isActive, scrollElement]);

    useImperativeHandle(
        ref,
        () => ({
            scrollToLetter: (letter, behavior = 'instant') => {
                const index = letterIndex.get(letter);
                if (index === undefined || !scrollElement) return;

                // Fixed row height, so index * size lands exactly; no sticky-header offset needed since it's a normal-flow sibling before the list.
                scrollElement.scrollTo({ top: index * ROW_HEIGHT, left: 0, behavior });
            },
        }),
        [letterIndex, scrollElement],
    );

    return (
        <ul style={{ position: 'relative', height: virtualizer.getTotalSize(), width: '100%' }}>
            {virtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index];
                const nextRow = rows[virtualRow.index + 1];

                return (
                    <li
                        key={String(virtualRow.key)}
                        data-index={virtualRow.index}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            transform: `translateY(${virtualRow.start}px)`,
                        }}
                    >
                        {row.type === 'header' ? (
                            <div className={styles.letterHeader}>{row.letter}</div>
                        ) : (
                            renderItem(row.item, {
                                isSectionEnd: !nextRow || nextRow.type === 'header',
                            })
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

export const AlphabeticVirtualizedList = forwardRef(AlphabeticVirtualizedListInner) as <T>(
    props: Props<T> & { ref?: Ref<AlphabeticVirtualizedListHandle> },
) => ReturnType<typeof AlphabeticVirtualizedListInner>;
